import { Component, OnInit } from '@angular/core';
import { EvenementService } from '../services/evenement.service';
import { ArtisteService } from '../services/artiste.service';
import { ParametreService } from '../services/parametre.service';
import { Evenement } from '../models/evenement.model';
import { Artiste } from '../models/artiste.model';
import { Parametre } from '../models/parametre.model';

interface Message {
  auteur: 'utilisateur' | 'assistant';
  texte: string;
}

@Component({
  selector: 'app-assistant',
  templateUrl: './assistant.component.html',
  styleUrls: ['./assistant.component.css']
})
export class AssistantComponent implements OnInit {

  ouvert = false;
  messages: Message[] = [];
  saisie = '';

  suggestions = [
    'Quels sont les prochains événements ?',
    'Combien coûte une place ?',
    'Qui sont les artistes ?',
    'Comment vous contacter ?'
  ];

  private evenements: Evenement[] = [];
  private artistes: Artiste[] = [];
  private parametres: Parametre | null = null;

  constructor(
    private evenementService: EvenementService,
    private artisteService: ArtisteService,
    private parametreService: ParametreService
  ) { }

  ngOnInit(): void {
    this.evenementService.getEvenements().subscribe(data => this.evenements = data);
    this.artisteService.getArtistes().subscribe(data => this.artistes = data);
    this.parametreService.getParametres().subscribe(data => this.parametres = data);

    this.messages.push({
      auteur: 'assistant',
      texte: "Bonjour ! Je suis l'assistant du Festival International de Sousse. Posez-moi une question, ou choisissez une suggestion ci-dessous."
    });
  }

  basculerOuverture(): void {
    this.ouvert = !this.ouvert;
  }

  envoyerSuggestion(texte: string): void {
    this.saisie = texte;
    this.envoyerMessage();
  }

  envoyerMessage(): void {
    const question = this.saisie.trim();
    if (!question) return;

    this.messages.push({ auteur: 'utilisateur', texte: question });
    this.saisie = '';

    // On simule un petit temps de "reflexion" pour que ca paraisse plus naturel
    setTimeout(() => {
      const reponse = this.genererReponse(question);
      this.messages.push({ auteur: 'assistant', texte: reponse });
    }, 400);
  }

  // Assistant base sur des regles simples (mots-cles), qui repond avec les
  // vraies donnees chargees depuis l'API. Ce n'est pas un vrai modele d'IA
  // (type ChatGPT/Claude) mais un systeme de correspondance par mots-cles.
  private genererReponse(question: string): string {
    const q = question.toLowerCase();

    if (q.includes('événement') || q.includes('evenement') || q.includes('programme') || q.includes('prochain')) {
      if (this.evenements.length === 0) return "Je n'ai pas encore d'informations sur le programme, réessayez dans un instant.";
      const liste = this.evenements
        .map(e => `• ${e.titre} — ${e.dateEvent} (${e.prix} TND)`)
        .join('\n');
      return `Voici les événements au programme :\n${liste}`;
    }

    if (q.includes('prix') || q.includes('coûte') || q.includes('coute') || q.includes('tarif') || q.includes('billet')) {
      if (this.evenements.length === 0) return "Je n'ai pas encore les tarifs, réessayez dans un instant.";
      const prix = this.evenements.map(e => e.prix);
      const min = Math.min(...prix), max = Math.max(...prix);
      return `Les places varient entre ${min} TND et ${max} TND selon le spectacle. Consultez la page Programme pour le détail de chaque événement.`;
    }

    if (q.includes('artiste') || q.includes('qui') || q.includes('chante') || q.includes('joue')) {
      if (this.artistes.length === 0) return "Je n'ai pas encore la liste des artistes, réessayez dans un instant.";
      const liste = this.artistes.map(a => `${a.nom} (${a.role})`).join(', ');
      return `Cette édition accueille : ${liste}. Voir la page Artistes pour en savoir plus.`;
    }

    if (q.includes('contact') || q.includes('téléphone') || q.includes('telephone') || q.includes('email') || q.includes('mail')) {
      if (this.parametres) {
        return `Vous pouvez nous contacter par e-mail à ${this.parametres.email} ou par téléphone au ${this.parametres.telephone}.`;
      }
      return "Les coordonnées de contact ne sont pas encore configurées.";
    }

    if (q.includes('où') || q.includes('ou') || q.includes('adresse') || q.includes('lieu')) {
      return `Le festival se déroule à : ${this.parametres?.adresse || 'Théâtre de Sidi Dhaher, Sousse, Tunisie'}.`;
    }

    if (q.includes('date') || q.includes('quand')) {
      return `Le festival a lieu ${this.parametres?.dates || 'du 10 au 25 juillet 2026'}.`;
    }

    if (q.includes('bonjour') || q.includes('salut') || q.includes('hello')) {
      return 'Bonjour ! Comment puis-je vous aider concernant le festival ?';
    }

    if (q.includes('merci')) {
      return 'Avec plaisir ! N\'hésitez pas si vous avez d\'autres questions.';
    }

    return "Je n'ai pas bien compris votre question. Essayez de me demander : le programme, les tarifs, les artistes, le lieu, les dates ou le contact.";
  }
}
